/**
 * Author: Claude Code
 * Generation: Authentication modal with WebSocket integration and redirect support
 * Version: v3.2.229
 * Modified: 2025-11-05 (Migrate to @react-oauth/google, remove deprecated APIs)
 */
import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useAuthStore } from '@store/AuthStore';
import { logCredentialReceived } from '../services/googleIdentity';

// Password validation: 7-15 chars, upper+lower+special
const validatePassword = (password: string): { valid: boolean; message: string } => {
  if (password.length < 7 || password.length > 15) {
    return { valid: false, message: 'Password must be 7-15 characters' };
  }
  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: 'Password must contain uppercase letter' };
  }
  if (!/[a-z]/.test(password)) {
    return { valid: false, message: 'Password must contain lowercase letter' };
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return { valid: false, message: 'Password must contain special character (!@#$%^&*...)' };
  }
  return { valid: true, message: 'Password strength: Strong' };
};

export default function AuthModal() {
  const navigate = useNavigate();
  const {
    showAuthModal,
    authMode,
    closeAuthModal,
    setAuthMode,
    login,
    register,
    isLoading,
    error,
    getAndClearRedirect,
  } = useAuthStore();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState<{ valid: boolean; message: string } | null>(null);

  // Handle Google Login success
  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      const token = credentialResponse?.credential;
      if (!token) {
        console.error('[AuthModal] No credential in response');
        return;
      }

      // Log credential reception for debugging
      logCredentialReceived(token);

      // Attempt login with token
      console.debug('[AuthModal] Google credential received, attempting login');
      const result = await useAuthStore.getState().loginWithGoogle(token);

      if (result?.success) {
        console.debug('[AuthModal] ✅ Google login successful');
        const redirectPath = getAndClearRedirect();
        closeAuthModal();
        if (redirectPath) navigate(redirectPath);
      } else {
        console.warn('[AuthModal] Google login returned unsuccessful');
      }
    } catch (err: any) {
      console.error('[AuthModal] Google login failed:', err?.message);
    }
  };

  // Handle Google Login errors
  const handleGoogleError = () => {
    console.error('[AuthModal] Google login error');
  };

  const handlePasswordChange = (password: string) => {
    setFormData({ ...formData, password });
    if (authMode === 'signup') {
      setPasswordValidation(validatePassword(password));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (authMode === 'signin') {
      const result = await login(formData.username, formData.password);
      if (result.success) {
        const redirectPath = getAndClearRedirect();
        closeAuthModal();
        if (redirectPath) {
          navigate(redirectPath);
        }
      }
    } else {
      // Signup mode: validate password strength
      const validation = validatePassword(formData.password);
      if (!validation.valid) {
        alert(validation.message);
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        alert('Passwords do not match');
        return;
      }
      const result = await register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
      });
      if (result.success) {
        const redirectPath = getAndClearRedirect();
        closeAuthModal();
        if (redirectPath) {
          navigate(redirectPath);
        }
      }
    }
  };


  if (!showAuthModal) return null;

  return (
    <div className={`modal-overlay ${showAuthModal ? 'active' : ''}`}>
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">
            {authMode === 'signin' ? 'Welcome Back' : 'Create Account'}
          </h2>
          <button className="modal-close" onClick={closeAuthModal}>×</button>
        </div>
        <div className="modal-body">
          {/* Google FedCM Compatible Login Button */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              use_fedcm_for_prompt={true}
            />
          </div>

          <div className="divider">or</div>

          {error && (
            <div className="error-message" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fee', color: '#c00', borderRadius: '8px' }}>
              {error}
            </div>
          )}

          <form id="authForm" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Username</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
            </div>

            {authMode === 'signup' && (
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="name@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={authMode === 'signin' ? 'text' : (showPassword ? 'text' : 'password')}
                  className="form-input"
                  placeholder={authMode === 'signin' ? 'Enter password' : '••••••••'}
                  value={formData.password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  style={{ paddingRight: '40px' }}
                  required
                />
                {authMode === 'signup' && (
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px'
                    }}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                )}
              </div>
              {/* Password strength indicator for signup */}
              {authMode === 'signup' && passwordValidation && (
                <div
                  style={{
                    marginTop: '8px',
                    padding: '8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: passwordValidation.valid ? '#e6f7e6' : '#ffe6e6',
                    color: passwordValidation.valid ? '#2d5f2d' : '#8b0000'
                  }}
                >
                  {passwordValidation.message}
                </div>
              )}
            </div>

            {authMode === 'signup' && (
              <div className="form-group">
                <label className="form-label">Confirm Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    style={{ paddingRight: '40px' }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px'
                    }}
                  >
                    {showConfirmPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
            )}

            {authMode === 'signin' && (
              <div className="checkbox-group">
                <input type="checkbox" id="rememberMe" />
                <label htmlFor="rememberMe">Remember me</label>
              </div>
            )}

            <button type="submit" className="btn btn-primary btn-md" style={{ width: '100%' }} disabled={isLoading}>
              {isLoading ? 'Loading...' : authMode === 'signin' ? 'Sign In' : 'Sign Up'}
            </button>
          </form>

          <div className="switch-text">
            <span>
              {authMode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
            </span>
            <button onClick={() => setAuthMode(authMode === 'signin' ? 'signup' : 'signin')}>
              {authMode === 'signin' ? 'Sign Up' : 'Sign In'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
